/**
 * Platform notifications: per-super-admin, per-event subscriptions to
 * platform-level events, delivered by email. The platform twin of the
 * portal's venue alerts (blazebite-customer-portal venue-notification.ts,
 * blazebite-functions src/notifications), sharing the same row and sent-log
 * shapes so the functions helpers can serve both.
 *
 * Paths (everything a super admin owns lives under their own user doc, so the
 * rules are simply "super admin AND it's your uid" for the whole subtree):
 *   platform_users/{uid}                                  per-admin platform prefs (future)
 *   platform_users/{uid}/notifications/{eventType}        subscription row (this app writes)
 *   platform_users/{uid}/notifications/{eventType}/sent/{id}  delivery log (functions-only write)
 *
 * The row id is the event type, so the panel writes and point-checks by path
 * with no lookup. `uid`, `eventType` and `enabled` are still stored top-level
 * so the functions fan-out is one collection-group equality query
 * (`notifications` where `eventType == E && enabled == true`; needs
 * collection-group scope on those two single-field indexes). No row means off.
 *
 * Subscriptions are personal: each super admin picks their own events and the
 * email goes to their own auth address, refreshed on every save.
 *
 * This file is the source of truth for the row shape and event catalog; the
 * functions copy (blazebite-functions src/notifications/types.ts, platform
 * section) must stay in step. Events are raised server-side through the
 * `platform_events` outbox (functions src/notifications/platform.ts); this
 * app only ever writes its own subscription rows.
 */

export type NotificationChannel = 'email';

export type PlatformNotificationEventType = 'customerSignup';

/** One super admin's setting for one platform event. */
export interface PlatformNotification {
  /** Doc id: the event type (see platformNotificationPath). */
  id?: string;
  uid: string;
  eventType: PlatformNotificationEventType;
  enabled: boolean;
  /** Delivery address. Snapshot of the auth email at last save. */
  email: string;
  displayName?: string | null;
  channel: NotificationChannel;
  createdAt: any;
  updatedAt: any;
}

export interface SubscriberIdentity {
  uid: string;
  email: string;
  displayName?: string | null;
}

export type PlatformNotificationSentStatus = 'pending' | 'sent' | 'failed';

/**
 * One alert queued for (then delivered or attempted to) one subscriber.
 * Path: platform_users/{uid}/notifications/{eventType}/sent/{sentId}.
 * Written server-side by the functions outbox consumer; this app only reads.
 */
export interface PlatformNotificationSent {
  id?: string;
  eventType: PlatformNotificationEventType;
  channel: NotificationChannel;
  to: string;
  /** Email subject. */
  title: string;
  /** One or two plain sentences: the same summary the email opens with. */
  body: string;
  status: PlatformNotificationSentStatus;
  error?: string | null;
  /** Structured facts about the event (the renderer payload). */
  context?: Record<string, unknown>;
  createdAt: any;
  sentAt: any;
}

/** How many sent alerts the panel shows across all events. */
export const RECENT_SENT_LIMIT = 50;

export interface PlatformNotificationEventDefinition {
  eventType: PlatformNotificationEventType;
  /** Short name shown next to the toggle. */
  label: string;
  /** One sentence: what you get and when. */
  description: string;
  /** What raises it, so the reader knows when to expect one. */
  detail: string;
}

/** The catalog of platform events a super admin can subscribe to. Order is display order. */
export const PLATFORM_NOTIFICATION_EVENTS: readonly PlatformNotificationEventDefinition[] = [
  {
    eventType: 'customerSignup',
    label: 'New customer signup',
    description: 'Get an email whenever a new customer signs up for BlazeBite.',
    detail: 'Raised once per prospect when the self-service signup creates their venue (onNewProspect). Includes the venue name, contact email, plan, and a link to the new venue.'
  }
];

export const PLATFORM_USERS_COLLECTION = 'platform_users';
export const PLATFORM_NOTIFICATIONS_SUBCOLLECTION = 'notifications';

/** Path segments of a (user, event) subscription row; deterministic so it's written in place, never duplicated. */
export function platformNotificationPath(uid: string, eventType: PlatformNotificationEventType): [string, string, string, string] {
  return [PLATFORM_USERS_COLLECTION, uid, PLATFORM_NOTIFICATIONS_SUBCOLLECTION, eventType];
}

/** Display name for an event type; falls back to the raw value for events this build doesn't know. */
export function platformEventLabel(eventType: string): string {
  return PLATFORM_NOTIFICATION_EVENTS.find(e => e.eventType === eventType)?.label ?? eventType;
}
